import React, { useState, useEffect, useMemo, memo } from 'react';
import StatsCardsDashboard from '../../../components/shared/StatsCardsDashboard';
import SplitStatsCard from '../../../components/shared/SplitStatsCard';
import SpendingDistributionChart from '../../../components/shared/SpendingDistributionChart';
import TopMerchantsPieChart from '../../../components/shared/TopMerchantsPieChart';
import transactionService from '../services/transactionService';
import {
  AttachMoney,
  TrendingDown,
  CalendarMonth,
  RepeatOneOn,
  Category,
  TrendingUp
} from '@mui/icons-material';

/**
 * ExpenseStatsDashboard Component
 * 
 * Displays key expense statistics at the top of the expenses section
 * Can calculate stats from filtered transactions if provided
 */
// Calculate expense statistics from transactions - moved outside component to avoid hoisting issues
// This function is pure and doesn't depend on component state
const calculateStatsFromTransactions = (transactions) => {
  // Filter for expenses (EXCLUDE transfers!)
  const expenseTransactions = transactions.filter(tx => {
    // Skip transfers
    if (tx.type === 'transfer') return false;
    
    // Include if explicitly marked as expense OR has negative amount
    return (typeof tx.amount === 'number' && tx.amount < 0) || tx.type === 'expense';
  });
  
  // Filter for income (EXCLUDE transfers!)
  const incomeTransactions = transactions.filter(tx => {
    // Skip transfers
    if (tx.type === 'transfer') return false;
    
    // Include if explicitly marked as income OR has positive amount
    return (typeof tx.amount === 'number' && tx.amount > 0) || tx.type === 'income';
  });
  
  console.log('💰 Transaction breakdown:', {
    total: transactions.length,
    expenses: expenseTransactions.length,
    income: incomeTransactions.length,
    sampleExpense: expenseTransactions[0],
    sampleIncome: incomeTransactions[0]
  });
  
  if (expenseTransactions.length === 0 && incomeTransactions.length === 0) {
    return {
      totalMonthlyExpenses: 0,
      totalMonthlyIncome: 0,
      incomeTransactionCount: 0,
      averageDailySpend: 0,
      monthOverMonthChange: 0,
      largestCategory: { name: '', amount: 0, percentage: 0 },
      transactionCount: 0,
      recurringExpenses: 0
    };
  }
  
  // Use expense transactions to find the most recent month
  // (fallback to income if no expenses)
  const txsForDateCalc = expenseTransactions.length > 0 ? expenseTransactions : incomeTransactions;
  
  // Find the most recent month with transactions
  const dates = txsForDateCalc.map(tx => new Date(tx.date));
  const latestDate = new Date(Math.max(...dates));
  latestDate.setHours(0, 0, 0, 0);
  
  // Get first and last day of the most recent month with data
  const recentMonthYear = latestDate.getFullYear();
  const recentMonth = latestDate.getMonth();
  const firstDayOfRecentMonth = new Date(recentMonthYear, recentMonth, 1);
  firstDayOfRecentMonth.setHours(0, 0, 0, 0);
  const lastDayOfRecentMonth = new Date(recentMonthYear, recentMonth + 1, 0);
  lastDayOfRecentMonth.setHours(23, 59, 59, 999);
  
  // Filter for most recent month transactions
  const recentMonthTransactions = expenseTransactions.filter(tx => {
    const txDate = new Date(tx.date);
    txDate.setHours(0, 0, 0, 0);
    return txDate >= firstDayOfRecentMonth && txDate <= lastDayOfRecentMonth;
  });
  
  // Get previous month for comparison
  const firstDayOfPrevMonth = new Date(recentMonthYear, recentMonth - 1, 1);
  firstDayOfPrevMonth.setHours(0, 0, 0, 0);
  const lastDayOfPrevMonth = new Date(recentMonthYear, recentMonth, 0);
  lastDayOfPrevMonth.setHours(23, 59, 59, 999);
  
  const prevMonthTransactions = expenseTransactions.filter(tx => {
    const txDate = new Date(tx.date);
    txDate.setHours(0, 0, 0, 0);
    return txDate >= firstDayOfPrevMonth && txDate <= lastDayOfPrevMonth;
  });
  
  console.log('📅 Month analysis:', {
    recentMonth: `${recentMonthYear}-${String(recentMonth + 1).padStart(2, '0')}`,
    recentMonthCount: recentMonthTransactions.length,
    prevMonth: `${recentMonthYear}-${String(recentMonth).padStart(2, '0')}`,
    prevMonthCount: prevMonthTransactions.length,
    totalExpenses: expenseTransactions.length
  });
  
  // Calculate income for recent month
  const recentMonthIncome = incomeTransactions.filter(tx => {
    const txDate = new Date(tx.date);
    txDate.setHours(0, 0, 0, 0);
    return txDate >= firstDayOfRecentMonth && txDate <= lastDayOfRecentMonth;
  });
  
  const totalMonthlyIncome = recentMonthIncome.reduce(
    (sum, tx) => sum + Math.abs(tx.amount || 0),
    0
  );
  
  // Calculate total monthly expenses for recent month
  const totalMonthlyExpenses = recentMonthTransactions.reduce(
    (sum, tx) => sum + Math.abs(tx.amount || 0), 
    0
  );
  
  // Calculate previous month total
  const prevMonthTotal = prevMonthTransactions.reduce(
    (sum, tx) => sum + Math.abs(tx.amount || 0), 
    0
  );
  
  // Calculate daily average (divide by number of days in the recent month)
  const daysInRecentMonth = lastDayOfRecentMonth.getDate();
  const averageDailySpend = daysInRecentMonth > 0 ? totalMonthlyExpenses / daysInRecentMonth : 0;
  
  // Calculate month over month change
  const monthOverMonthChange = prevMonthTotal > 0 
    ? ((totalMonthlyExpenses - prevMonthTotal) / prevMonthTotal) * 100 
    : 0;
  
  console.log('💵 Expense calculations:', {
    recentMonthTotal: totalMonthlyExpenses,
    prevMonthTotal: prevMonthTotal,
    daysInMonth: daysInRecentMonth,
    dailyAverage: averageDailySpend,
    monthOverMonth: monthOverMonthChange
  });
  
  // Find largest category (from recent month only)
  const categoryTotals = {};
  recentMonthTransactions.forEach(tx => {
    const categoryName = tx.categoryRef?.name || tx.category?.name || 'Uncategorized';
    if (!categoryTotals[categoryName]) {
      categoryTotals[categoryName] = 0;
    }
    categoryTotals[categoryName] += Math.abs(tx.amount || 0);
  });
  
  const largestCategoryEntry = Object.entries(categoryTotals).reduce(
    (max, [name, amount]) => amount > max.amount ? { name, amount } : max,
    { name: '', amount: 0 }
  );
  
  const largestCategory = {
    name: largestCategoryEntry.name,
    amount: largestCategoryEntry.amount,
    percentage: totalMonthlyExpenses > 0 
      ? (largestCategoryEntry.amount / totalMonthlyExpenses) * 100 
      : 0
  };
  
  // Calculate recurring expenses (same merchant, same amount, 2+ times)
  const recurringGroups = {};
  expenseTransactions.forEach(tx => {
    const merchantKey = (tx.description || '').toLowerCase().trim();
    const amountKey = Math.round(Math.abs(tx.amount || 0));
    const key = `${merchantKey}-${amountKey}`;
    
    if (merchantKey && amountKey > 0) {
      if (!recurringGroups[key]) {
        recurringGroups[key] = [];
      }
      recurringGroups[key].push(tx);
    }
  });
  
  const recurringKeys = Object.keys(recurringGroups).filter(
    key => recurringGroups[key].length >= 2
  );
  
  // Calculate recurring expenses for recent month only
  const recurringExpenses = recentMonthTransactions
    .filter(tx => {
      const merchantKey = (tx.description || '').toLowerCase().trim();
      const amountKey = Math.round(Math.abs(tx.amount || 0));
      const key = `${merchantKey}-${amountKey}`;
      return recurringKeys.includes(key);
    })
    .reduce((sum, tx) => sum + Math.abs(tx.amount || 0), 0);
  
  console.log('🔁 Recurring expenses:', {
    recurringKeys: recurringKeys.length,
    recurringTotal: recurringExpenses,
    percentOfTotal: (recurringExpenses / totalMonthlyExpenses * 100).toFixed(1) + '%'
  });
  
  return {
    totalMonthlyExpenses,
    totalMonthlyIncome,
    incomeTransactionCount: recentMonthIncome.length,
    averageDailySpend,
    monthOverMonthChange,
    largestCategory,
    transactionCount: recentMonthTransactions.length,
    recurringExpenses
  };
};

const ExpenseStatsDashboard = ({ filteredTransactions = null, allTransactions = [], filters = {} }) => {
  const [loading, setLoading] = useState(true);
  const [expenseStats, setExpenseStats] = useState({
    totalMonthlyExpenses: 0,
    totalMonthlyIncome: 0,
    incomeTransactionCount: 0,
    averageDailySpend: 0,
    monthOverMonthChange: 0,
    largestCategory: {
      name: '',
      amount: 0,
      percentage: 0
    },
    transactionCount: 0,
    recurringExpenses: 0
  });

  // Memoize filter detection to avoid recalculating on every render
  const hasActiveFilters = useMemo(() => {
    return filters && Object.keys(filters).length > 0 && Object.values(filters).some(v => {
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === 'string') return v.trim() !== '' && v !== 'all';
      return v !== null && v !== undefined;
    });
  }, [filters]);

  // Memoize which transactions to use for calculations
  // This prevents recalculating when only the current page changes
  const transactionsToUse = useMemo(() => {
    // Use filteredTransactions ONLY if filters are actually active
    // Otherwise, always use allTransactions for accurate metrics
    if (hasActiveFilters && filteredTransactions && Array.isArray(filteredTransactions) && filteredTransactions.length > 0) {
      return filteredTransactions;
    }
    if (allTransactions && Array.isArray(allTransactions) && allTransactions.length > 0) {
      return allTransactions;
    }
    return [];
  }, [hasActiveFilters, filteredTransactions, allTransactions]);

  // Memoize the expensive stats calculation
  // Only recalculates when transactionsToUse actually changes
  const calculatedStats = useMemo(() => {
    if (transactionsToUse.length > 0) {
      return calculateStatsFromTransactions(transactionsToUse);
    }
    return {
      totalMonthlyExpenses: 0,
      totalMonthlyIncome: 0,
      incomeTransactionCount: 0,
      averageDailySpend: 0,
      monthOverMonthChange: 0,
      largestCategory: { name: '', amount: 0, percentage: 0 },
      transactionCount: 0,
      recurringExpenses: 0
    };
  }, [transactionsToUse]);

  // Update state only when calculated stats change
  useEffect(() => {
    try {
      setLoading(true);
      
      console.log('📊 ExpenseStatsDashboard calculating stats:', {
        filteredTransactionsLength: filteredTransactions?.length || 0,
        allTransactionsLength: allTransactions?.length || 0,
        filtersActive: hasActiveFilters,
        transactionsToUseLength: transactionsToUse.length
      });
      
      console.log('📊 Using transactions:', {
        count: transactionsToUse.length,
        source: hasActiveFilters ? 'filtered' : 'all',
        hasActiveFilters
      });
      
      setExpenseStats(calculatedStats);
      console.log('📊 Calculated stats:', calculatedStats);
    } catch (error) {
      console.error('Error setting expense statistics:', error);
      setExpenseStats({
        totalMonthlyExpenses: 0,
        totalMonthlyIncome: 0,
        incomeTransactionCount: 0,
        averageDailySpend: 0,
        monthOverMonthChange: 0,
        largestCategory: { name: '', amount: 0, percentage: 0 },
        transactionCount: 0,
        recurringExpenses: 0
      });
    } finally {
      setLoading(false);
    }
  }, [calculatedStats, hasActiveFilters, filteredTransactions, allTransactions, transactionsToUse.length]);

  const formatCurrency = (value) => {
    // Handle cases where value is undefined, null, or not a number
    if (value === undefined || value === null || isNaN(value)) {
      return '$0.00';
    }
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercentage = (value) => {
    // Handle cases where value is undefined, null, or not a number
    if (value === undefined || value === null || isNaN(value)) {
      return '0.0%';
    }
    return `${value.toFixed(1)}%`;
  };

  // Split card data for Income/Expense overview
  const splitCardData = {
    top: {
      title: 'Income',
      value: formatCurrency(expenseStats.totalMonthlyIncome),
      description: `${expenseStats.incomeTransactionCount} transactions`,
      icon: TrendingUp,
      color: 'green'
    },
    bottom: {
      title: 'Expenses',
      value: formatCurrency(expenseStats.totalMonthlyExpenses),
      description: `${expenseStats.transactionCount} transactions`,
      icon: TrendingDown,
      color: 'red'
    },
    tooltip: 'Overview of your income and expenses for the most recent month with transaction data. Income shows positive transactions (deposits, paychecks). Expenses show negative transactions or type "expense" (purchases, bills).'
  };

  // Prepare split card for Daily Average + Recurring Expenses
  const dailyRecurringSplitCard = {
    title: "Last Month's Spending Analysis",
    top: {
      value: formatCurrency(expenseStats.averageDailySpend),
      description: 'Daily Average',
      icon: CalendarMonth,
      color: 'orange'
    },
    bottom: {
      value: formatCurrency(expenseStats.recurringExpenses),
      description: `Recurring (${formatPercentage(expenseStats.recurringExpenses / Math.max(expenseStats.totalMonthlyExpenses, 1) * 100)} of total)`,
      icon: RepeatOneOn,
      color: 'emerald'
    },
    tooltip: 'Daily Average: Your average spending per day for the recent month. Recurring: Total spent on subscriptions and recurring bills (same merchant + amount).'
  };

  // Memoize recent month expense transactions for charts
  // Only recalculates when transactionsToUse changes
  const recentMonthExpenseTransactions = useMemo(() => {
    if (transactionsToUse.length === 0) return [];
    
    // Filter for expenses only (exclude transfers)
    const expenses = transactionsToUse.filter(tx => {
      if (tx.type === 'transfer') return false;
      return (typeof tx.amount === 'number' && tx.amount < 0) || tx.type === 'expense';
    });
    
    // Get most recent month
    if (expenses.length === 0) return [];
    
    const dates = expenses.map(tx => new Date(tx.date));
    const latestDate = new Date(Math.max(...dates));
    const recentMonthYear = latestDate.getFullYear();
    const recentMonth = latestDate.getMonth();
    const firstDayOfRecentMonth = new Date(recentMonthYear, recentMonth, 1);
    firstDayOfRecentMonth.setHours(0, 0, 0, 0);
    const lastDayOfRecentMonth = new Date(recentMonthYear, recentMonth + 1, 0);
    lastDayOfRecentMonth.setHours(23, 59, 59, 999);
    
    return expenses.filter(tx => {
      const txDate = new Date(tx.date);
      txDate.setHours(0, 0, 0, 0);
      return txDate >= firstDayOfRecentMonth && txDate <= lastDayOfRecentMonth;
    });
  }, [transactionsToUse]);

  return (
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 pt-6 bg-transparent">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {/* Split Income/Expense Card */}
        <SplitStatsCard
          top={splitCardData.top}
          bottom={splitCardData.bottom}
          tooltip={splitCardData.tooltip}
        />
        
        {/* Spending Distribution Chart */}
        <SpendingDistributionChart
          transactions={recentMonthExpenseTransactions}
          tooltip="Shows how many transactions fall into each spending range for the most recent month. Helps you understand if you have many small purchases vs few large ones. Colors indicate spending intensity (green = small, red/purple = large)."
        />
        
        {/* Daily Average + Recurring Expenses Split Card */}
        <SplitStatsCard
          top={dailyRecurringSplitCard.top}
          bottom={dailyRecurringSplitCard.bottom}
          tooltip={dailyRecurringSplitCard.tooltip}
        />
        
        {/* Top Merchants Pie Chart */}
        <TopMerchantsPieChart
          transactions={recentMonthExpenseTransactions}
          tooltip="Shows your top 5 merchants by total spending for the recent month. Helps you identify where most of your money goes and spot opportunities to cut back."
          limit={5}
        />
      </div>
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
// Only re-renders when props actually change
export default memo(ExpenseStatsDashboard, (prevProps, nextProps) => {
  // Custom comparison function for better performance
  // Only re-render if these props actually change
  const propsEqual = 
    prevProps.allTransactions === nextProps.allTransactions &&
    prevProps.filteredTransactions === nextProps.filteredTransactions &&
    JSON.stringify(prevProps.filters) === JSON.stringify(nextProps.filters);
  
  return propsEqual;
}); 